import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import SpeedDial from '@mui/material/SpeedDial';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type Location, useLocation, useNavigate } from 'react-router-dom';
import { setAllVehiclesState, setLeadStatus } from '../_flux/Actions';
import { type FluxStore } from '../_flux/Store';
import DialogBox from '../_tools/DialogBox';
import Image from '../_tools/Image';
import { isAdmin, isBewertung, isProgrammierung, isTester, isVKL } from '../_tools/Session';
import Speechbubble from '../_tools/Speechbubble';
import { betterUseNavigate, checkUndefinedNullOrEmpty, checkUndefinedOrNull, isLocalServer } from '../_tools/Tools';
import { getVertrag } from '../_tools/Vertrag';
import Iconify from '../components/iconify/component';
import { type IconifyProps } from '../components/iconify/types';
import { type CustomTheme } from '../provider/ThemeProvider';
import CarBewertungIcon from '../svg/icons/CarBewertungIcon';
import CrispIcon from '../svg/icons/CrispIcon';
import { LEAD, STATE_BESTAND, STATE_BEWERTET, STATE_GEPLANT, STATE_VERKAUFT, STATE_VORLAUF } from '../vars';
import UserMenu from './userMenu';

export type NavMenuItem = {
    additionalOnClick?: () => void;
    disabled?: boolean;
    disabledText?: string;
    icon: IconifyProps;
    link?: string;
    onClick?: () => void;
    selected?: boolean;
    state?: Record<string, unknown>;
    testid?: string;
    title: string;
};

const Navigation = () => {
    const leadstatus = useSelector((state: FluxStore) => state?.leadstatus);
    const unchangedData = useSelector((state: FluxStore) => state?.unchangedData);
    const updateRequired = useSelector((state: FluxStore) => state?.updateRequired);
    const userSettings = useSelector((state: FluxStore) => state?.userSettings);
    const vertrage = useSelector((state: FluxStore) => state?.vertrage);
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState<any>(0);
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [openFAB, setOpenFAB] = useState<boolean>(false);
    const [showUpdateDialog, setShowUpdateDialog] = useState<boolean>(false);
    const [tabs, setTabs] = useState<any>([]);
    const [title, setTitle] = useState<string>('');
    const navigate = useNavigate();
    const loc: Location & { state: { [index: string]: string | number | boolean } } = useLocation();
    const theme: CustomTheme = useTheme();
    const xs = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const sm = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    const heightToolbar = xs ? 48 : sm ? 56 : 64;
    const widthSidebar = xs ? 0 : sm ? 48 : 56;
    /* const logo = !LEAD
         ? userSettings?.main && userSettings?.main?.darkMode
             ? '/images/logos/support_white.png'
             : '/images/logos/support.png'
         : userSettings?.main && userSettings?.main?.darkMode
             ? '/images/logos/cardesk_white.png'
             : '/images/logos/cardesk.png'; */
    const logo = !LEAD ? '/images/logos/support.png' : '/images/logos/logo.png';

    const getMenuItems = (big) => {
        const upperItems: NavMenuItem[] = [
            { title: 'Dashboard', link: '/dashboard', selected: location?.href?.includes('/dashboard'), icon: 'mdi:view-dashboard' },
            LEAD && (isTester() || getVertrag('lead', vertrage)) && { title: 'Leads', link: '/leads', selected: location?.href?.includes('/leads'), icon: 'mdi:card-account-mail' },
            LEAD && {
                title: 'Fahrzeuge',
                link: '/vehicles',
                selected: location?.href?.includes('/vehicles') && !(location?.href?.includes('/vehicles/neu') && loc?.state?.activeTab === 5) && !location?.href?.includes('/bewertung'),
                icon: 'mdi:car',
                additionalOnClick: () => dispatch(setAllVehiclesState(undefined)),
            },
            { title: 'Kunden', link: LEAD ? '/clients' : '/firmen', selected: location?.href?.includes(LEAD ? '/clients' : '/firmen'), icon: 'mdi:account-multiple' },
            !LEAD && { title: 'Karte', link: '/maps', selected: location?.href?.includes('/maps'), icon: 'mdi:map' },
            !LEAD && { title: 'Logs', link: '/logs', selected: location?.href?.includes('/logs'), icon: 'mdi:format-list-bulleted-type' },
            !LEAD && { title: 'Serverstatus', link: '/server', selected: location?.href?.includes('/server'), icon: 'mdi:dns' },
            !LEAD && { title: 'Ausstattung', link: '/ausstattung', selected: location?.href?.includes('/ausstattung'), icon: 'mdi:shape' },
            LEAD &&
                (isTester() || isBewertung(vertrage)) && {
                    title: 'Fahrzeugbewertung',
                    link: '/vehicles/neu',
                    state: { activeTab: 5 },
                    selected: (location?.href?.includes('/vehicles/neu') && loc?.state?.activeTab === 5) || location?.href?.includes('/bewertung'),
                    icon: CarBewertungIcon,
                },
            LEAD && (isTester() || getVertrag('carscanner', vertrage)) && { title: 'CarScanner', link: '/qr', selected: location?.href?.includes('/qr'), icon: 'mdi:qrcode-scan' },
        ];

        const lowerItems: NavMenuItem[] = [
            updateRequired && { title: 'Update verfügbar', onClick: () => setShowUpdateDialog(true), icon: 'mdi:update' },
            isProgrammierung() && { title: 'Showreel', link: '/showreel', selected: location?.href?.includes('/showreel'), icon: 'mdi:play-box-outline' },
            (isAdmin() || isVKL()) && { title: 'Einstellungen', link: '/einstellungen', selected: location?.href?.includes('/einstellungen'), icon: 'mdi:cog' },
            LEAD && {
                title: 'Support',
                onClick: () => {
                    const crisp: HTMLElement = document.querySelector('.crisp-client #crisp-chatbox > div > a');
                    if (crisp) crisp.click();
                },
                icon: CrispIcon,
            },
            !LEAD && { title: 'Globale Benachrichtigungen', link: '/notifications', state: undefined, selected: location?.href?.includes('/notifications'), icon: 'mdi:bell-cog' },
            !LEAD && { title: 'Globale Einstellungen', link: '/global-settings', state: undefined, selected: location?.href?.includes('/global-settings'), icon: 'mdi:database-cog' },
        ];

        return (
            <>
                <List key="upperMenu" disablePadding>
                    {upperItems.map(
                        (item) =>
                            item && (
                                <ListItemButton
                                    key={'upperItem_' + item.title}
                                    sx={{ minWidth: !big ? 0 : undefined, py: 1.5 }}
                                    className="mainNavButton"
                                    selected={item.selected}
                                    onClick={() => {
                                        if (!checkUndefinedOrNull(item.additionalOnClick)) {
                                            item.additionalOnClick();
                                        }
                                        betterUseNavigate(navigate, loc?.state, { replace: false, pathname: item.link, state: item.state }, unchangedData);
                                    }}
                                >
                                    <ListItemIcon color="inherit" data-testid={item.title.toLowerCase()}>
                                        <Iconify icon={item.icon} />
                                    </ListItemIcon>
                                    {big && <ListItemText primary={item.title} primaryTypographyProps={{ sx: { color: 'inherit' } }} />}
                                </ListItemButton>
                            )
                    )}
                </List>
                <div key="divider" style={{ height: '100%' }} />
                <List key="lowerMenu" disablePadding>
                    {lowerItems.map((item) => {
                        if (item) {
                            const update = item.title === 'Update verfügbar';
                            const support = item.title === 'Support';
                            return (
                                <ListItemButton
                                    key={'lowerItem_' + item.title}
                                    sx={{
                                        background: (update && theme?.palette?.warning?.main) || (support && theme?.palette?.crisp?.main),
                                        color: update || support ? '#fff' : undefined,
                                        minWidth: !big ? 0 : undefined,
                                        py: 1.5,
                                        ...(update || support
                                            ? {
                                                  '&:hover': {
                                                      background: (update && theme?.palette?.warning?.light) || (support && theme?.palette?.crisp?.light),
                                                  },
                                              }
                                            : {}),
                                    }}
                                    id={update ? 'updateNotification' : support ? 'supportNotification' : undefined}
                                    className="mainNavButton"
                                    selected={item.selected}
                                    onClick={item.onClick ? item.onClick : () => betterUseNavigate(navigate, loc?.state, { replace: false, pathname: item.link, state: item.state }, unchangedData)}
                                >
                                    <ListItemIcon data-testid={item.title.toLowerCase()} style={{ color: 'inherit' }}>
                                        <Iconify icon={item.icon} />
                                    </ListItemIcon>
                                    {big && <ListItemText primary={item.title} primaryTypographyProps={{ sx: { color: 'inherit' } }} />}
                                    {update && (
                                        <Speechbubble
                                            key="updateBubble"
                                            color={theme?.palette?.warning?.main}
                                            anchor="#updateNotification"
                                            origin="right"
                                            title="Update verfügbar!"
                                            primary="Zum Anwenden hier klicken"
                                            onClick={
                                                item.onClick ? item.onClick : () => betterUseNavigate(navigate, loc?.state, { replace: false, pathname: item.link, state: item.state }, unchangedData)
                                            }
                                        />
                                    )}
                                </ListItemButton>
                            );
                        }
                    })}
                </List>
            </>
        );
    };

    const setTitleAndTab = () => {
        let id = 0;
        if (
            !location?.href?.includes('/login') &&
            !location?.href?.includes('/loginr') &&
            !location?.href?.includes('/dashboard') &&
            !location?.href?.includes('/client') &&
            !location?.href?.includes('/notifications')
        ) {
            if (location?.href?.includes('/leads/')) {
                if (location?.href?.includes('/offen') || (location?.href?.includes('/lead/') && leadstatus === 0)) id = 0;
                else if (location?.href?.includes('/closed') || (location?.href?.includes('/lead/') && leadstatus === 1)) id = 1;
                else if (location?.href?.includes('/statistik')) id = 2;
            } else if (location?.href?.includes('/vehicles/')) {
                if (location?.href?.includes('/bestand') || loc?.state?.status === STATE_BESTAND) id = 0;
                else if (location?.href?.includes('/geplant') || loc?.state?.status === STATE_GEPLANT || loc?.state?.status === STATE_VORLAUF) id = 1;
                else if (location?.href?.includes('/bewertet') || loc?.state?.status === STATE_BEWERTET) id = 2;
                else if (location?.href?.includes('/verkauft') || loc?.state?.status === STATE_VERKAUFT) id = 3;
                else if (location?.href?.includes('/statistik')) id = 4;
            } else if (location?.href?.includes('/firmen')) {
                if (location?.href?.endsWith('/firmen')) id = 0;
                else if (location?.href?.includes('/vertrag')) id = 1;
            } else if (location?.href?.includes('/logs')) {
                if (location?.href?.includes('/export')) id = 0;
                else if (location?.href?.includes('/error')) id = 1;
            } else if (location?.href?.includes('/einstellungen/')) {
                if (location?.href?.includes('/firma')) id = 0;
                else if (location?.href?.includes('/benutzer')) id = 1;
                else if (location?.href?.includes('/druck')) id = 2;
                else if (location?.href?.includes('/standardwerte')) id = 3;
                else if (location?.href?.includes('/bilder')) id = 4;
                else if (location?.href?.includes('/events')) id = 5;
                else if (location?.href?.includes('/kunden')) id = 6;
                else if (location?.href?.includes('/vertrage')) id = 7;
            } else if (location?.href?.includes('/qr/')) {
                if (location?.href?.includes('/carscanner')) id = 0;
                else if (location?.href?.includes('/inventur')) id = 1;
            } else if (location?.href?.includes('/showreel')) {
                if (location?.href?.endsWith('/boxmaterial')) id = 0;
                if (location?.href?.endsWith('/dialogbox')) id = 1;
                if (location?.href?.endsWith('/inputfield')) id = 2;
                if (location?.href?.endsWith('/typography')) id = 3;
                if (location?.href?.endsWith('/farbschema')) id = 4;
                if (location?.href?.endsWith('/portale')) id = 5;
                if (location?.href?.endsWith('/tablematerial')) id = 6;
                if (location?.href?.endsWith('/speechbubble')) id = 7;
                if (location?.href?.endsWith('/dnd')) id = 8;
                if (location?.href?.endsWith('/datagrid')) id = 9;
                if (location?.href?.endsWith('/effizienz')) id = 10;
                if (location?.href?.endsWith('/siegel')) id = 11;
            } else if (location?.href?.includes('/ausstattung')) {
                if (location?.href?.endsWith('/zuordnungen')) id = 0;
                if (location?.href?.endsWith('/texte')) id = 1;
            }
        }

        let titleNav = '';
        if (location?.href?.includes('/dashboard')) titleNav = 'Dashboard';
        else if (location?.href?.includes('/leads/')) titleNav = 'Leadmanagement';
        else if (location?.href?.includes('/vehicles/neu')) {
            if (loc?.state?.activeTab === 1) titleNav = 'Fahrzeuganlage per VIN-Abfrage (DAT)';
            // else if (loc?.state?.activeTab === 2) titleNav = 'Fahrzeuganlage per VIN-Abfrage (VW)';
            else if (loc?.state?.activeTab === 3) titleNav = 'Fahrzeuganlage per VIN-Abfrage (Mercedes)';
            // else if (loc?.state?.activeTab === 4) titleNav = 'Fahrzeuganlage per VIN-Abfrage (Schwacke)';
            else if (loc?.state?.activeTab === 5) titleNav = 'Fahrzeugbewertung';
            else if (loc?.state?.activeTab === 6) titleNav = 'Fahrzeuganlage Geführt';
            else if (loc?.state?.activeTab === 7) titleNav = 'Fahrzeuganlage per HSN-TSN';
            else if (loc?.state?.activeTab === 8) titleNav = 'Fahrzeuganlage Manuell';
            else titleNav = 'Fahrzeuganlage';
        } else if (location?.href?.includes('/bewertung')) titleNav = 'Fahrzeugbewertung';
        else if (location?.href?.includes('/vehicles/')) titleNav = 'Fahrzeuge';
        else if (location?.href?.includes('/logs')) titleNav = 'Logs';
        else if (location?.href?.includes('/server')) titleNav = 'Serverstatus';
        else if (location?.href?.includes('/client')) titleNav = 'Kundenmanagement';
        else if (location?.href?.includes('/firmen')) titleNav = 'Firmenmanagement';
        else if (location?.href?.includes('/maps')) titleNav = 'Maps';
        else if (location?.href?.includes('/qr')) titleNav = 'CarScanner';
        else if (location?.href?.includes('/einstellungen/')) titleNav = 'Einstellungen';
        else if (location?.href?.includes('/global-settings/')) titleNav = 'Globale Einstellungen';
        else if (location?.href?.includes('/notifications')) titleNav = 'Globale Benachrichtigungen';
        else if (location?.href?.includes('/logout')) titleNav = 'Logout';
        else if (location?.href?.includes('/showreel')) titleNav = 'Showreel';
        else if (location?.href?.includes('/ausstattung')) titleNav = 'Ausstattung';

        const tabItems = [];
        if (location?.href?.includes('/leads')) {
            if (getVertrag('lead', vertrage) || isTester()) {
                tabItems.push([{ pathname: '/leads/offen' }, 'Offen']);
                tabItems.push([{ pathname: '/leads/closed' }, 'Beendet']);
                if (isVKL()) {
                    tabItems.push([{ pathname: '/leads/statistik' }, 'Auswertungen']);
                }
            }
        } else if (location?.href?.includes('/vehicles') && !location?.href?.includes('/vehicles/neu') && !location?.href?.includes('/bewertung')) {
            tabItems.push([{ pathname: '/vehicles/bestand' }, 'Bestand']);
            tabItems.push([{ pathname: '/vehicles/geplant' }, 'Geplant']);
            tabItems.push([{ pathname: '/vehicles/bewertet' }, 'Bewertet']);
            tabItems.push([{ pathname: '/vehicles/verkauft' }, 'Verkauft']);
            tabItems.push([{ pathname: '/vehicles/statistik' }, 'Auswertungen']);
        } else if (location?.href?.includes('/firmen')) {
            tabItems.push([{ pathname: '/firmen' }, 'Firmen']);
            tabItems.push([{ pathname: '/firmen/vertrag' }, 'Verträge']);
        } else if (location?.href?.includes('/logs')) {
            tabItems.push([{ pathname: '/logs/export' }, 'Exporte']);
            tabItems.push([{ pathname: '/logs/error' }, 'Fehler']);
        } else if (location?.href?.includes('/qr')) {
            tabItems.push([{ pathname: '/qr/scanner' }, 'CarScanner']);
            tabItems.push([{ pathname: '/qr/inventur' }, 'Inventur']);
        } else if (location?.href?.includes('/showreel')) {
            tabItems.push([{ pathname: '/showreel/boxmaterial' }, 'BoxMaterial']);
            tabItems.push([{ pathname: '/showreel/dialogbox' }, 'DialogBox']);
            tabItems.push([{ pathname: '/showreel/inputfield' }, 'InputField']);
            tabItems.push([{ pathname: '/showreel/typography' }, 'Typography']);
            tabItems.push([{ pathname: '/showreel/farbschema' }, 'Farbschema']);
            tabItems.push([{ pathname: '/showreel/portale' }, 'Portale']);
            tabItems.push([{ pathname: '/showreel/tablematerial' }, 'TableMaterial']);
            tabItems.push([{ pathname: '/showreel/speechbubble' }, 'Speechbubble']);
            tabItems.push([{ pathname: '/showreel/dnd' }, 'DnD']);
            tabItems.push([{ pathname: '/showreel/datagrid' }, 'DataGrid']);
            tabItems.push([{ pathname: '/showreel/effizienz' }, 'Effizienz']);
            tabItems.push([{ pathname: '/showreel/siegel' }, 'Siegel']);
        } else if (location?.href?.includes('/einstellungen')) {
            tabItems.push([{ pathname: '/einstellungen/firma' }, 'Firma']);
            tabItems.push([{ pathname: '/einstellungen/benutzer' }, 'Benutzer']);
            tabItems.push([{ pathname: '/einstellungen/druck' }, 'Druck & Mail']);
            tabItems.push([{ pathname: '/einstellungen/standardwerte' }, 'Standardwerte']);
            tabItems.push([{ pathname: '/einstellungen/bilder' }, 'Bilder']);
            tabItems.push([{ pathname: '/einstellungen/events' }, 'Ereignisse']);
            if (isLocalServer()) tabItems.push([{ pathname: '/einstellungen/vertrage' }, 'Verträge']);
        } else if (location?.href?.includes('/global-settings')) {
            tabItems.push([{ pathname: '/global-settings/psh' }, 'PSH']);
        } else if (location?.href?.includes('/ausstattung')) {
            tabItems.push([{ pathname: '/ausstattung/zuordnungen' }, 'Zuordnungen']);
            tabItems.push([{ pathname: '/ausstattung/texte' }, 'Ausstattungstexte']);
        }

        const body = document.documentElement;
        if (
            (location?.href?.includes('/leads/') && (location?.href?.endsWith('/offen') || location?.href?.endsWith('/closed') || location?.href?.includes('/lead/'))) ||
            (location?.href?.includes('/vehicles/') &&
                (((location?.href?.endsWith('/bewertet') || location?.href?.endsWith('/bestand') || location?.href?.endsWith('/verkauft')) &&
                    userSettings?.fahrzeugliste &&
                    userSettings?.fahrzeugliste?.listenansicht) ||
                    location?.href?.endsWith('/geplant'))) ||
            (location?.href?.includes('/vehicle/') && !(location?.href?.endsWith('/verkaufsprovision') || location?.href?.endsWith('/bewertung')))
        )
            body.classList.add('toastHigher');
        else body.classList.remove('toastHigher');

        if (location?.href?.includes('/leads/closed')) dispatch(setLeadStatus(1));
        else if (location?.href?.includes('/leads/offen')) dispatch(setLeadStatus(0));

        setTitle(titleNav);
        setActiveTab(id);
        setTabs(tabItems);
        setOpenDrawer(false);
    };

    const speeddial = (mode) => {
        if (mode === 'hidden') {
            return !(
                location?.href?.includes('/leads/offen') ||
                (location?.href?.includes('/vehicles') &&
                    !location?.href?.includes('/vehicle/') &&
                    !location?.href?.includes('/verkauft') &&
                    !location?.href?.includes('/statistik') &&
                    !location?.href?.includes('/bewertung') &&
                    !location?.href?.includes('/reservierung') &&
                    !location?.href?.includes('/zahlungsplan') &&
                    !location?.href?.includes('/verkaufsprovision') &&
                    !location?.href?.includes('/neu')) ||
                location?.href?.includes('/clients') ||
                location?.href?.includes('/firmen') ||
                (location?.href?.endsWith('/einstellungen/benutzer') && isAdmin()) ||
                location?.href?.includes('/qr/code')
            );
        } else {
            if (location?.href?.includes('/leads/offen')) betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/leads/neu' }, unchangedData);
            else if (location?.href?.includes('/vehicles/bewertet') || location?.href?.includes('/vehicles/geplant') || location?.href?.includes('/vehicles/bestand'))
                betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/vehicles/neu' }, unchangedData);
            else if (location?.href?.includes('/clients')) betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/clients/new' }, unchangedData);
            else if (location?.href?.includes('/firmen')) betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/firmen/client/0' }, unchangedData);
            else if (location?.href?.endsWith('/einstellungen/benutzer') && isAdmin())
                betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/einstellungen/benutzer', state: { aktUser: 0 } }, unchangedData);
            else if (location?.href?.includes('/qr/code')) betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/qr/scanner' }, unchangedData);
            else setOpenFAB(true);
        }
    };

    useEffect(setTitleAndTab, [loc, userSettings, leadstatus]);

    return (
        <div className="noPrint">
            <AppBar position="fixed" sx={{ color: 'common.white' }}>
                <Toolbar>
                    <IconButton style={{ marginRight: 32 }} color="inherit" onClick={() => setOpenDrawer(true)} size="large">
                        <Iconify icon="mdi:menu" />
                    </IconButton>
                    {!checkUndefinedNullOrEmpty(tabs) ? (
                        <Tabs className="navTabs" variant="scrollable" value={activeTab} onChange={(e, i) => setActiveTab(i)} scrollButtons={false}>
                            {tabs.map((tab, i) => (
                                <Tab
                                    key={tab[0].pathname}
                                    value={i}
                                    onClick={() => {
                                        betterUseNavigate(navigate, loc?.state, { ...tab[0], replace: false, state: { status: undefined } }, unchangedData);
                                    }}
                                    label={<div data-testid={tab[0].pathname}>{tab[1]}</div>}
                                />
                            ))}
                        </Tabs>
                    ) : (
                        <Typography variant="h6" color="inherit">
                            {title}
                        </Typography>
                    )}
                    <div style={{ flexGrow: 1 }} />
                    <UserMenu />
                </Toolbar>
            </AppBar>
            <SwipeableDrawer disableDiscovery disableSwipeToOpen anchor="left" open={openDrawer} onOpen={() => setOpenDrawer(true)} onClose={() => setOpenDrawer(false)}>
                <Typography align="center" variant="body2">
                    <Image contain height={heightToolbar + 'px'} src={logo} alt="AHD Cars" sx={{ mb: 2 }} />
                </Typography>
                {getMenuItems(true)}
            </SwipeableDrawer>
            {!xs && (
                <Paper
                    style={{
                        position: 'absolute',
                        top: heightToolbar + 'px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100% - ' + heightToolbar + 'px)',
                        width: widthSidebar,
                        padding: '8px 0',
                    }}
                >
                    {getMenuItems(false)}
                </Paper>
            )}
            <SpeedDial
                ariaLabel="Mehr"
                id="speeddial"
                key="speeddial"
                icon={<Iconify data-testid="speeddial" icon={location?.href?.includes('/qr/code') ? 'mdi:qrcode-scan' : 'mdi:plus'} />}
                style={{
                    position: 'fixed',
                    right: theme.spacing(2),
                    zIndex: 999,
                    bottom: theme.spacing(
                        (location?.href?.includes('/vehicles') && !location?.href?.includes('/vehicle/') && userSettings?.fahrzeugliste && userSettings?.fahrzeugliste?.listenansicht) ||
                            (location?.href?.includes('/leads') && !location?.href?.includes('/lead/'))
                            ? 9
                            : 2
                    ),
                }}
                hidden={speeddial('hidden')}
                onBlur={() => setOpenFAB(false)}
                onClose={() => setOpenFAB(false)}
                onFocus={() => setOpenFAB(true)}
                onMouseEnter={() => setOpenFAB(true)}
                onMouseLeave={() => setOpenFAB(false)}
                onClick={speeddial}
                open={openFAB}
                direction="left"
            />
            {showUpdateDialog ? (
                <DialogBox
                    dialogData={{
                        warning: true,
                        title: 'Update laden?',
                        message: 'ACHTUNG! Beim Updaten der Anwendung werden alle nicht gespeicherten Änderungen gelöscht!',
                        negativeAnswer: () => setShowUpdateDialog(false),
                        positiveAnswer: () => location.reload(),
                    }}
                />
            ) : undefined}
        </div>
    );
};

export default Navigation;
